import * as React from 'react';
import getCurrentSelectedFolderId from 'mini-mail-search-view/lib/utils/getCurrentSelectedFolderId';
import getFolderDisplayNameFromId from 'owa-mail-store/lib/utils/getFolderDisplayNameFromId';
import getFolderScopeItems from 'mini-mail-search-view/lib/utils/getFolderScopeItems';
import setStaticFolderScopeIdUtil from 'mini-mail-search-view/lib/utils/setStaticFolderScopeIdUtil';
import strings from 'owa-strings/owa-mini-strings';
import { addAriaLiveLabel } from 'mini-accessibility';
import { format } from 'owa-strings';
import { getIconClassName } from '@uifabric/styling';
import { getMailSearchStore } from 'mini-3s-mail-search-store/lib/store/Store';
import isUnifiedSearchEnabled from 'mini-search-common/lib/utils/isUnifiedSearchEnabled';
import { lazyShowSelectableListInBottomSheet } from 'mini-bottomsheet-selectablelist';
import { MiniIcons } from 'owa-mini-icons';
import { observer } from 'mobx-react';
import { SelectableListItems } from 'mini-selectable-list';
import { enterKeyHandler } from 'window-utils/lib/keyHandlerUtils';

const styles = require('./MailSearchFilter.scss');
const classNames = require('classnames/bind').bind(styles);

export interface MailSearchFilterProps {
    onMailFilterClicked?: (folderId: string) => void;
}

/**
 * Folder scope filter component to display search filter header
 */
@observer
export default class MailSearchFilter extends React.Component<MailSearchFilterProps, {}> {
    private folderScopeRef = React.createRef<HTMLDivElement>();
    private bottomSheetItems: SelectableListItems[] = [];

    private getBottomSheetItems(): SelectableListItems[] {
        const bottomSheetItems = getFolderScopeItems().map(
            (folderScopeItem): SelectableListItems => ({
                icon: folderScopeItem.icon,
                key: folderScopeItem.folderId,
                name: folderScopeItem.folderDisplayName,
            })
        );
        return bottomSheetItems;
    }

    // WI #89122 : Clean up following 2 functions once unified search is 100%
    private onFilterSelect = selectedItems => {
        const { onMailFilterClicked } = this.props;

        /*
         * Announce which folder was selected upon selection
         */
        const selectedFolderName = this.bottomSheetItems.find(
            (item: SelectableListItems) => item.key === selectedItems[0]
        )?.name;
        addAriaLiveLabel(format(strings.selectedFolderAriaLiveLabel, selectedFolderName));

        onMailFilterClicked
            ? onMailFilterClicked(selectedItems[0])
            : setStaticFolderScopeIdUtil(selectedItems[0]);
    };

    private get getFolderScopeId(): string {
        return isUnifiedSearchEnabled()
            ? getMailSearchStore().currentFolderScopeId
            : getCurrentSelectedFolderId();
    }

    private openFolderScopePane = () => {
        this.bottomSheetItems = this.getBottomSheetItems();
        lazyShowSelectableListInBottomSheet.importAndExecute(
            {
                items: this.bottomSheetItems,
                selectedItems: [this.getFolderScopeId],
                onSelect: this.onFilterSelect,
                getAriaLabelForItem: (title, isSelected) =>
                    isSelected
                        ? format(strings.selectedFolderAriaLabel, title)
                        : format(strings.unselectedFolderAriaLabel, title),
            },
            { elementToFocusOnClose: this.folderScopeRef, id: 'MailSearchFolderScopeOptions' }
        );
    };

    render() {
        const folderName: string = getFolderDisplayNameFromId(this.getFolderScopeId);
        return (
            <div className={styles.folderScopeContainer}>
                <div className={styles.folderScopeLeftItems}>
                    <span>{strings.resultsIn}</span>
                    <div
                        tabIndex={0}
                        role="button"
                        aria-haspopup={true}
                        aria-label={format(strings.folderScopeAriaLabel, folderName)}
                        ref={this.folderScopeRef}
                        className={styles.folderOptions}
                        onClick={this.openFolderScopePane}
                        onKeyPress={enterKeyHandler(this.openFolderScopePane)}>
                        <span className={styles.folderName}>{folderName}</span>
                        <span
                            className={classNames(
                                'ms-Icon',
                                styles.folderNameChevron,
                                getIconClassName(MiniIcons.ChevronDown)
                            )}
                        />
                    </div>
                </div>
            </div>
        );
    }
}
